import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { PersonaService, ManagedVendorService } from '@shared/service';
import { AuthService } from '@core/service/auth.service';

interface NavItem {
  label: string;
  path: string;
  iconKey: string;
  badge?: string;
}

/**
 * Vendor sidebar (mockup `<aside class="sidebar">`). 248px, fixed. Brand + nav + the
 * "Managing: {vendor}" mecard with switch-vendor / customer-app / admin-console actions.
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  @Input() open = false;
  @Output() close = new EventEmitter<void>();
  /** Opens the vendor-switch modal (built in Phase 4); Phase 1 routes to /auth/login. */
  @Output() switchVendor = new EventEmitter<void>();

  readonly personaSvc = inject(PersonaService);
  readonly mv = inject(ManagedVendorService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  /**
   * Current URL as a signal so the active-item highlight updates on every
   * navigation under OnPush (reading `router.url` directly wouldn't re-run CD).
   */
  private readonly url = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  );

  readonly items: NavItem[] = [
    { label: 'Dashboard',    path: '/vendor/dashboard', iconKey: 'home' },
    { label: 'Booking',      path: '/vendor/bookings',  iconKey: 'calendar' },
    { label: 'Wallet',       path: '/vendor/wallet',    iconKey: 'wallet' },
    { label: 'My Services',  path: '/vendor/services',  iconKey: 'grid' },
    { label: 'Website',      path: '/vendor/website',   iconKey: 'globe' },
    { label: 'Partnerships', path: '/vendor/partners',  iconKey: 'people' },
  ];

  onSwitchVendor() { this.switchVendor.emit(); }
  isActive(path: string): boolean {
    const url = this.url();
    // Segment-boundary match so a path is never a false prefix of a sibling.
    return url === path || url.startsWith(path + '/');
  }
  go(path: string) {
    this.close.emit();
    void this.router.navigateByUrl(path);
  }
  openAdminConsole() {
    // If an admin is mid-onboarding (acting as the new vendor), leaving to the console ends
    // that session and restores the admin persona; a no-op otherwise.
    this.personaSvc.endAdminOnboarding();
    void this.router.navigateByUrl('/admin/overview');
  }
  logout() { void this.auth.logout(); }
}
